import { Injectable, InjectionToken, inject } from '@angular/core';
import { DataDictionary } from '../models/data-dictionary.model';
import type { Workbook } from 'exceljs';

type WorkbookCtor = new () => InstanceType<typeof Workbook>;

export const EXCEL_WORKBOOK_CTOR = new InjectionToken<WorkbookCtor>('ExcelWorkbookCtor');

interface ExcelJsModule { Workbook?: WorkbookCtor; default?: { Workbook?: WorkbookCtor } }

/** Resolves Workbook from exceljs (CJS default export vs namespace). */
export async function resolveExcelJsWorkbookCtor(injected?: WorkbookCtor): Promise<WorkbookCtor> {
  if (injected) {
    return injected;
  }
  const mod = (await import('exceljs')) as ExcelJsModule;
  const exceljs = mod.default ?? mod;
  const WorkbookCtor = exceljs.Workbook;
  if (typeof WorkbookCtor !== 'function') {
    throw new Error('exceljs Workbook constructor is not available');
  }
  return WorkbookCtor;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private readonly injectedWorkbookCtor = inject(EXCEL_WORKBOOK_CTOR, { optional: true });

  exportToExcel(dictionaries: DataDictionary[]): void {
    if (dictionaries.length === 0) return;

    const csvContent = dictionaries.map(dict => {
      const headers = ['Field Name', 'Data Type', 'Description'];
      const rows = dict.fields.map(field => [
        this.escapeCSV(field.fieldName),
        this.escapeCSV(field.dataType),
        this.escapeCSV(field.description),
      ].join(','));

      return [headers.join(','), ...rows].join('\n');
    }).join('\n\n');

    this.downloadFile(
      csvContent,
      `data-dictionary-${Date.now()}.csv`,
      'text/csv'
    );
  }

  exportMultipleToExcel(dictionaries: DataDictionary[]): void {
    const content = dictionaries.map(dict => {
      const headers = ['Field Name', 'Data Type', 'Description'];
      const rows = dict.fields.map(field => [
        this.escapeCSV(field.fieldName),
        this.escapeCSV(field.dataType),
        this.escapeCSV(field.description),
      ].join(','));

      return `\n=== ${dict.name} ===\n${[headers.join(','), ...rows].join('\n')}`;
    }).join('\n');

    this.downloadFile(
      content,
      `data-dictionaries-export-${Date.now()}.csv`,
      'text/csv'
    );
  }

  async exportMultipleToExcelWorkbook(dictionaries: DataDictionary[]): Promise<void> {
    if (dictionaries.length === 0) return;

    const WorkbookCtor = await resolveExcelJsWorkbookCtor(this.injectedWorkbookCtor ?? undefined);
    const workbook = new WorkbookCtor();
    const usedNames: string[] = [];

    dictionaries.forEach(dict => {
      let sheetName = dict.name.substring(0, 31).replace(/[/\\?*[\]]/g, '_');
      const originalName = sheetName;
      let suffix = 1;
      while (usedNames.includes(sheetName)) {
        sheetName = `${originalName.substring(0, 28)}_${suffix++}`;
      }
      usedNames.push(sheetName);

      const worksheet = workbook.addWorksheet(sheetName);
      worksheet.columns = [
        { width: 25 },
        { width: 15 },
        { width: 50 },
      ];

      worksheet.addRow(['Field Name', 'Data Type', 'Description']);
      dict.fields.forEach(field => {
        worksheet.addRow([
          field.fieldName,
          field.dataType,
          field.description,
        ]);
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `data-dictionaries-export-${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportSampleDataToCsv(dictionary: DataDictionary): void {
    if (!dictionary.sampleData || dictionary.sampleData.length === 0) return;

    const fieldNames = dictionary.fields.map(f => f.fieldName);
    const headers = fieldNames.map(name => this.escapeCSV(name)).join(',');

    const rows = dictionary.sampleData.map(row => {
      return fieldNames.map(fieldName => {
        const value = row[fieldName];
        return this.escapeCSV(value !== null && value !== undefined ? String(value) : '');
      }).join(',');
    });

    const csvContent = [headers, ...rows].join('\n');

    this.downloadFile(
      csvContent,
      `${dictionary.name}-sample-data-${Date.now()}.csv`,
      'text/csv'
    );
  }

  private escapeCSV(value: string): string {
    if (!value) return '';
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  private downloadFile(content: string, filename: string, type: string): void {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
